/**
 * A trade between filing and history: the awaiting card, the open window, and the counters.
 *
 * These are the only two cards in the app that are DIFFERENT OBJECTS to different readers, and
 * that is not decoration. Before affirmation the eight uninvolved GMs must not see the trade at
 * all (2022-trade-declarations §1). After it, everybody sees the deal — but a counter-offer is
 * private to the man who sent it and the man it was sent to (2015-II-2), including from the other
 * party to the trade, who finds out only when one is accepted, in public, and then gets his four
 * hours to match. The filtering is `canSeeTrade` and `countersFor`; this file only draws.
 *
 * THE CLOCK IS THE HEADLINE and it is stated with its origin: "Clock started when Ryan affirmed,
 * 20 Aug at 9:12pm". Every year somebody counts from the wrong message. The awaiting card's
 * header carries the same instant when a commissioner has recorded one for a deal declared
 * elsewhere, and carries only "filed by Matt" when there is nothing to count from yet — because
 * a trade nobody has affirmed has no clock, and printing a time beside it would imply one.
 */

import { useEffect, useMemo, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { holdingsFor } from '../domain/picks2026.js';
import {
  farmCapCheck, keeperEffect, keepersAfterTrade, keepersSent, movesSay,
} from '../domain/tradeKeepers.js';
import {
  controlledAssets, farmCount, tradePhase, type FarmRow, type RosterRow,
} from '../domain/tradeRoster.js';
import { gmOfTeam } from '../domain/identity.js';
import farmLog from '../data/farm-log.json';
import keeperRows from '../data/keepers-2026.json';

const FARM = farmLog as unknown as FarmRow[];
const ROSTER = keeperRows as unknown as RosterRow[];
import { shortfallSays } from '../domain/keeperSlots.js';
import { bareName } from './TradeAssets.js';
import {
  acceptCounter, canCounter, countdown, countersFor, declineCounter, firstPickInvolved, isParty,
  matchQualifies, matchRight, msLeft, parties, pickCoords, picksUntilClose, tradeState, vetoTally,
  windowShape, windowWhy,
  type MatchRight, type WindowCtx,
} from '../domain/tradeWindow.js';
import { AssetLine, assetPhrase } from './TradeAssets.js';
import { ADD, AssetStack } from './TradeAssetEditor.js';
import { GmBadge } from './GmBadge.js';
import { IndexMark, Rule } from './RuleText.js';
import { bothZones } from '../domain/deadlines.js';
import type {
  KeeperDeclaration, LiveTradeAsset, LiveTradeCounter, LiveTradeDoc, Role,
} from '../store/types.js';
import type { SlotKeeper } from '../domain/keeperSlots.js';

interface Ctx extends WindowCtx {
  gm: string;
  role: Role;
  /** Who holds which pick right now — the ledger plus every trade settled since. */
  ownership: ReadonlyMap<string, string>;
  /** When slates lock, for what a GM may put in a counter. */
  keeperDeadline?: string | null;
  /** A GM's submitted slate, priced and charged, for the keeper chooser on affirmation. */
  slateOf?: (gm: string) => {
    decl: KeeperDeclaration | undefined;
    keepers: SlotKeeper[];
    ownedRounds: number[];
    now: Map<string, number>;
  };
  /**
   * The trade a replacement came out of, by id. Needed only for the matching tail, where the
   * bumped partner's rights are defined against the ORIGINAL package and not against the trade
   * he is looking at.
   */
  originalOf?: (id: string) => LiveTradeDoc | undefined;
  onSave: (t: LiveTradeDoc) => void;
  /** Write it into the 109-row ledger. Commissioner-only — see `firestore.rules`, `match /trades`. */
  onSettle: (t: LiveTradeDoc) => void;
}

/** A minute is the smallest unit any clock here shows, so it is the only tick worth taking. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function LiveTradeCard({ t, ctx }: { t: LiveTradeDoc; ctx: Ctx }) {
  const now = useMinute();
  const isCommish = ctx.role === 'commissioner' || ctx.role === 'admin';
  const state = tradeState(t, { ...ctx, now });
  if (state === 'awaiting') return <Awaiting t={t} ctx={ctx} />;
  /*
   * A CLOSED TRADE DOES NOT VANISH. Only a commissioner may write the ledger, so the last thing
   * that happens to a trade is somebody performing it — and between the clock running out and that
   * moment, the two parties need to see where their deal is. It used to be filtered off the page
   * entirely at hour 48, which from the parties' side is indistinguishable from having been lost.
   */
  if (state === 'closed' || state === 'vetoed' || state === 'replaced') {
    return <Finished t={t} ctx={ctx} state={state} isCommish={isCommish} />;
  }
  return <Open t={t} ctx={ctx} now={now} isCommish={isCommish} />;
}

/* -- the end of a window ----------------------------------------------------------------------- */

const ENDED: Record<string, { label: string; why: string }> = {
  closed: {
    label: 'Window closed',
    why: 'Nobody countered it out and it was not vetoed, so it stands. It files itself into the '
      + 'log the next time a commissioner has the app open.',
  },
  vetoed: {
    label: 'Vetoed',
    why: 'Six of the eight uninvolved GMs declared inside the twenty hours, so this and every '
      + 'counter opened by it are null and void.',
  },
  replaced: {
    label: 'Countered out',
    why: 'A party accepted a counter-offer, which replaces this one. The trade that goes through '
      + 'is the last one accepted.',
  },
};

function Finished(
  { t, ctx, state, isCommish }:
  { t: LiveTradeDoc; ctx: Ctx; state: string; isCommish: boolean },
) {
  const copy = ENDED[state]!;
  const file = () => ctx.onSettle(t);
  /*
   * A CLOSED WINDOW SAYS WHICH WAY IT CLOSED. `closed` covers both the clock running out and a
   * commissioner ending it, and those are different facts about the same trade — the second one
   * has a person's name on it, and the card that says "nobody countered it out" would be reading
   * the league's silence into a decision somebody made for them.
   */
  const why = t.forced
    ? `${managerShort(t.forced.by)} closed the window at ${stamp(t.forced.at)}, before it ran out. `
      + 'The counter and veto period ended there. It files itself into the log the next time a '
      + 'commissioner has the app open.'
    : copy.why;
  return (
    <section className={`trlive ended ${state}`}>
      <header className="trlivehead">
        <span className="trstate">{copy.label}</span>
        <span className="mut">
          {managerShort(t.sides[0].gm)} and {managerShort(t.sides[1].gm)}
        </span>
        <span className="trsep" aria-hidden="true" />
        <span className="mut">
          {t.agreedAt ? `window ran from ${stamp(t.agreedAt)}` : `filed ${stamp(t.filedAt)}`}
        </span>
        {t.forced && <span className="trforced">closed early</span>}
        <span className="spacer" />
        {state === 'vetoed' && <Rule id="2016-I-3" />}
        {state === 'replaced' && <Rule id="2015-II-2" />}
      </header>
      <Gets t={t} />
      <footer className="trlivefoot">
        <p className="mut">{why}</p>
        {/*
          THE BUTTON STAYS, AND IT IS NO LONGER THE WAY THIS NORMALLY HAPPENS. Filing runs on its
          own in a commissioner's session (`Trades.tsx`), so by the time anybody reads this card the
          trade has usually gone. What is left for the button is the case that failed — a write
          refused, a connection that dropped — where the alternative to a retry is a deal sitting in
          limbo with nothing anybody can do about it. It is secondary because pressing it is now the
          exception.
        */}
        {state === 'closed' && isCommish && (
          <button type="button" className="btn" onClick={file}>File it now</button>
        )}
      </footer>
    </section>
  );
}

/* -- who takes the hit --------------------------------------------------------------------------- */

/**
 * The GM's choice of which keeper moves, made where he affirms.
 *
 * 2015-III-7 forces SOME re-charge when a keeper's pick leaves and leaves the choice of which
 * keeper to the GM — Rajan: "you can effectively escalate whomever you want, based on their
 * overlaps." The resolver's default is the arrangement every other screen would compute, and it
 * is shown first; each keeper who is still legal somewhere else gets a select of the rounds he
 * could be charged to instead, and every change is re-fitted so the list on screen is always a
 * slate that works. What he settles on is written to the trade as `keeperPins` and applied when
 * the window closes.
 *
 * Only rounds he will OWN after the trade are offered, at or before the keeper's cost, and a
 * round already taken by a keeper he did not move is named rather than hidden — taking it is
 * legal, it just moves the other man, and the re-fit shows where.
 */
function KeeperChooser(
  { gm, t, ctx, pins, onPins }: {
    gm: string; t: LiveTradeDoc; ctx: Ctx;
    pins: Readonly<Record<string, number>>;
    onPins: (p: Record<string, number>) => void;
  },
) {
  const who = gm.toUpperCase();
  const side = t.sides.findIndex((x) => x.gm.toUpperCase() === who) as 0 | 1;
  const slate = ctx.slateOf?.(who);
  const effect = useMemo(() => {
    if (!slate || side < 0) return null;
    const other = ctx.slateOf?.(t.sides[1 - side]!.gm);
    const outgoing = keepersSent(t.sides[side]!.sends, slate.keepers).map((k) => k.player);
    const incoming = other ? keepersSent(t.sides[1 - side]!.sends, other.keepers) : [];
    const keepers = keepersAfterTrade(slate.keepers, outgoing, incoming);
    if (keepers.length === 0) return null;
    const rounds = (assets: readonly LiveTradeAsset[]) => assets
      .filter((a) => a.kind === 'pick' && a.text.includes(String(t.season)))
      .map((a) => pickCoords(a.text)?.round)
      .filter((r): r is number => r !== undefined);
    const gives = rounds(t.sides[side]!.sends);
    const gets = rounds(t.sides[1 - side]!.sends);
    const after = [...slate.ownedRounds];
    for (const r of gives) { const i = after.indexOf(r); if (i >= 0) after.splice(i, 1); }
    after.push(...gets);
    return {
      keepers,
      after,
      result: keeperEffect(who, keepers, slate.now, slate.ownedRounds, gives, gets, pins),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slate?.decl?.updatedAt, t.id, pins]);

  if (!effect) return null;
  const { keepers, after, result } = effect;
  if (result.shortfall) {
    return (
      <p className="trparity bad">
        <span className="ico" aria-hidden="true">✕</span>
        Your keepers no longer fit once this settles: {shortfallSays(result.shortfall)}
        {' '}<Rule id="2015-III-7" />
      </p>
    );
  }
  const nothingMoves = result.moves.length === 0;
  const owned = [...new Set(after)].sort((a, b) => a - b);
  const takenBy = new Map([...result.placed].map(([p, r]) => [r, p]));

  return (
    <div className="trchooser">
      <p className="mut">
        {nothingMoves
          ? 'Your keepers stay where they are.'
          : `Your keepers after this trade: ${movesSay(result.moves, bareName)}. `
            + 'Change who takes the hit below, or leave it — either way it is written to your '
            + 'slate when the window closes.'}
      </p>
      {!nothingMoves && (
        <ul className="trchooserlist">
          {keepers.map((k) => {
            const at = result.placed.get(k.player);
            const options = owned.filter((r) => r <= k.costRound);
            return (
              <li key={k.player}>
                <span className="trchoosername">{bareName(k.player)}</span>
                <span className="mut">costs Rd {k.costRound}</span>
                <select value={at ?? ''}
                  onChange={(e) => {
                    const r = Number(e.target.value);
                    const next = { ...pins };
                    if (r === at) delete next[k.player]; else next[k.player] = r;
                    onPins(next);
                  }}>
                  {options.map((r) => {
                    const holder = takenBy.get(r);
                    return (
                      <option key={r} value={r}>
                        Round {r}{holder && holder !== k.player ? ` · ${bareName(holder)} is there` : ''}
                      </option>
                    );
                  })}
                </select>
                {pins[k.player] !== undefined && <span className="chip tiny">your choice</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* -- awaiting affirmation ---------------------------------------------------------------------- */

function Awaiting({ t, ctx }: { t: LiveTradeDoc; ctx: Ctx }) {
  const isCommish = ctx.role === 'commissioner' || ctx.role === 'admin';
  const other = t.sides[1].gm;
  const filer = managerShort(t.filedBy);
  /*
   * WHO TAKES THE HIT goes on the trade with the affirmation. The affirming GM chooses for his
   * own keepers; a commissioner affirming on his behalf may choose for either side, since he is
   * the one in the room with both of them. Stored per GM so settlement applies each man's own.
   */
  const [pins, setPins] = useState<Record<string, Record<string, number>>>(t.keeperPins ?? {});
  const pinsFor = (gm: string) => pins[gm.toUpperCase()] ?? {};
  const setPinsFor = (gm: string, p: Record<string, number>) =>
    setPins({ ...pins, [gm.toUpperCase()]: p });
  const affirm = () => ctx.onSave({
    ...t,
    agreedAt: new Date().toISOString(),
    agreedSource: 'affirmation',
    agreedBy: ctx.gm.toUpperCase(),
    ...(Object.keys(pins).length ? { keeperPins: pins } : {}),
  });
  const iAmHim = other.toUpperCase() === ctx.gm.toUpperCase();
  const hours = windowShape(Date.now(), ctx).totalH;
  const iMadeIt = t.filedBy.toUpperCase() === ctx.gm.toUpperCase();
  const [confirmBin, setConfirmBin] = useState(false);
  const bin = () => ctx.onSave({ ...t, withdrawn: true });

  return (
    <section className="trlive awaiting">
      <header className="trlivehead">
        <span className="trstate">Awaiting {managerShort(other)}</span>
        <span className="mut">window not started</span>
        <span className="trsep" aria-hidden="true" />
        {/*
          One timestamp, two provenances (Tim, 22 August). It is here only when a commissioner has
          recorded a deal struck outside the app — a GM filing in the app supplies none, because
          filing is the declaration and the affirmation is what starts the clock.
        */}
        <span className="mut">
          {t.agreedAt ? `agreed ${stamp(t.agreedAt)} · filed by ${filer}` : `filed by ${filer}`}
        </span>
        <span className="spacer" />
        <span className="mut trvis">Visible to {managerShort(t.sides[0].gm)},
          {' '}{managerShort(other)} and the commissioners</span>
      </header>

      <Gets t={t} />

      {/* The keepers question, put to the man answering it. */}
      {(iAmHim || isCommish) && ctx.slateOf && (
        <KeeperChooser gm={other} t={t} ctx={ctx} pins={pinsFor(other)}
          onPins={(p) => setPinsFor(other, p)} />
      )}
      {isCommish && ctx.slateOf && (
        <KeeperChooser gm={t.sides[0].gm} t={t} ctx={ctx} pins={pinsFor(t.sides[0].gm)}
          onPins={(p) => setPinsFor(t.sides[0].gm, p)} />
      )}

      <footer className="trlivefoot">
        <p className="mut">
          {/*
            `?? 48` was here and was wrong exactly when it mattered: on draft day `totalH` is null
            because the window is counted in picks, and the fallback confidently promised a
            48-hour window to a trade that has none.
          */}
          Filed by {filer} — nothing counts until {managerShort(other)} affirms these terms, and
          the counter window{hours === null
            ? ' — which on draft day runs until the first pick in it is selected — '
            : ` of ${hours} hours `}starts when he does.
        </p>
        {/*
          BINNING A MIS-FILED TRADE IS NOT A WITHDRAWAL.
          2019-II-2 governs backing out of a trade that has been "mutually agreed-upon", and its
          own §2 says that means a public declaration AND a confirmation by both parties. Nothing
          here has been confirmed by anybody: the filer typed it, the other man has not answered,
          and the clock has not started. So the filer may take it back, and a commissioner may take
          back one he recorded on somebody's behalf — which is also exactly what `firestore.rules`
          already permitted and no button had ever offered. The moment it is affirmed this control
          is gone and 2019-II-2's two grounds are the only way out.
        */}
        {(isCommish || iMadeIt) && (
          confirmBin ? (
            <span className="trbinask">
              <span className="mut">Take it back?</span>
              <button type="button" className="btn danger sm" onClick={bin}>Yes, bin it</button>
              <button type="button" className="btn sm" onClick={() => setConfirmBin(false)}>Keep it</button>
            </span>
          ) : (
            <button type="button" className="btn sm" onClick={() => setConfirmBin(true)}>
              Withdraw it
            </button>
          )
        )}
        {(isCommish || iAmHim) && (
          <button type="button" className={`btn ${iAmHim ? 'primary' : 'commish'}`} onClick={affirm}>
            {iAmHim ? 'Affirm these terms' : `Affirm for ${managerShort(other)}`}
          </button>
        )}
      </footer>
    </section>
  );
}

/* -- the open window --------------------------------------------------------------------------- */

function Open(
  { t, ctx, now, isCommish }: { t: LiveTradeDoc; ctx: Ctx; now: number; isCommish: boolean },
) {
  const w = windowShape(t.agreedAt!, ctx);
  const veto = vetoTally(t, { ...ctx, now });
  const [a, b] = t.sides;
  /*
   * ON DRAFT DAY THE CLOCK IS A PICK COUNT. 2017-II-3 §2 measures the window against the board, so
   * the headline is "3 picks" over "until 2.4 is made" and not a time — a countdown in hours on
   * draft night is worse than no countdown, because it is confidently wrong about the one thing
   * everybody is watching.
   */
  const onBoard = w.phase === 'draft-day';
  const picks = onBoard ? picksUntilClose(t, t.season, { ...ctx, now }) : null;
  const first = onBoard && ctx.clock ? firstPickInvolved(t, t.season, ctx.clock) : null;
  const mine = countersFor(t, ctx.gm, isCommish);

  const declareVeto = () => ctx.onSave({
    ...t, vetoes: [...new Set([...t.vetoes, ctx.gm.toUpperCase()])],
  });
  const [correcting, setCorrecting] = useState(false);
  /*
   * PUSHING IT THROUGH IS TWO CLICKS, DELIBERATELY (Tim, 8 September: "add functionality as commish
   * to push the trade through before the window is complete").
   *
   * The window is the eight uninvolved GMs' counter and veto right, and this ends it early. That
   * is a legitimate thing for a commissioner to do — everybody has said their piece, nobody is
   * countering, and the board is waiting — but it is not a thing to do by brushing a link on a
   * phone. So the link asks, and the confirm names what is being cut short.
   */
  const [forcing, setForcing] = useState(false);
  const pushThrough = () => {
    ctx.onSave({
      ...t,
      forced: { by: ctx.gm.toUpperCase(), at: new Date().toISOString() },
    });
    setForcing(false);
  };

  return (
    <section className="trlive open">
      <header className="trwinhead">
        <div className="trclock">
          <span className="trstate live"><i className="dot" aria-hidden="true" />Window open</span>
          {onBoard ? (
            <>
              <strong>{picks === null ? '—' : picks === 0 ? 'on the clock' : `${picks} pick${picks === 1 ? '' : 's'}`}</strong>
              <span className="mut">
                {first ? `until ${first.label} is made` : 'until the pick in it is made'}
              </span>
            </>
          ) : (
            <>
              <strong>{countdown(msLeft(t, { ...ctx, now }))}</strong>
              <span className="mut">{w.label}</span>
            </>
          )}
        </div>
        <span className="trsep tall" aria-hidden="true" />
        <div className="trwinbody">
          <p className="trdeal">
            {managerShort(a.gm)} and {managerShort(b.gm)} — {assetPhrase(a.sends)} for
            {' '}{assetPhrase(b.sends)}
          </p>
          <p className="mut trwhen">
            {/*
              FOUR SENTENCES, because there are four ways a clock can start and saying the wrong
              one is how a window gets counted from the wrong moment.

              A commissioner affirming on an unreachable GM's behalf is not the GM affirming —
              printing "when Ryan affirmed" while Ryan was on a plane is a claim about Ryan that
              the app cannot support. And a trade born from an accepted counter INHERITED its
              window; saying its counterparty affirmed it would be describing an act that never
              happened, on a clock that started before this trade existed.
            */}
            {t.derivedFrom
              ? `Clock inherited from the trade this replaced, which started ${stamp(t.agreedAt!)}. `
                + 'Accepting a counter does not buy a fresh window.'
              : t.agreedSource === 'commissioner'
                ? `Clock started from the time a commissioner recorded, ${stamp(t.agreedAt!)}.`
                : t.agreedBy && t.agreedBy.toUpperCase() !== b.gm.toUpperCase()
                  ? `Clock started when ${managerShort(t.agreedBy)} affirmed for ${managerShort(b.gm)}, ${stamp(t.agreedAt!)}.`
                  : `Clock started when ${managerShort(b.gm)} affirmed, ${stamp(t.agreedAt!)}.`}
            {' '}
            {w.phase === 'offseason'
              ? 'Offseason, so counters stay open the full 48 hours rather than closing at 20.'
              : w.phase === 'in-season'
                ? 'In season, so counters close at 20 hours and the last 4 are for the other GM to match.'
                : (() => {
                  /*
                   * "DRAFT DAY" IS NOT WHEN THIS WINDOW STARTS (Tim, 8 September: "john and
                   * matt's trade wasn't on the day of the draft though").
                   *
                   * It was not, and the app was right anyway — 2018-II-2 §2 hands the LAST 48
                   * HOURS before the first pick, and the draft itself, to 2017-II-3's pick-count
                   * rules. A deal agreed at seven the evening before is inside that band. But
                   * the sentence said "Draft day", which about a trade struck the day before is
                   * a plain untruth, and the one a manager would take to the committee.
                   *
                   * So it says which of the two it is. The rule is the same either way; what
                   * changes is whether the reader can check it.
                   */
                  const start = ctx.draftDate ? Date.parse(ctx.draftDate) : NaN;
                  const before = Number.isFinite(start) && Date.parse(t.agreedAt!) < start;
                  const when = before
                    ? 'Agreed inside the 48 hours before the draft, so 2018-II-2 §2 puts it under'
                      + ' the draft\u2019s own rules'
                    : 'Draft day';
                  return first
                    ? `${when}: there is no clock — counters run until ${first.label} — pick `
                      + `${first.overall} — is selected, and they run while its holder is on the clock.`
                    : `${when}: there is no clock — counters run until the pick in it is selected.`;
                })()}
          </p>
          <p className="trveto">
            <span className="trvetolab">Vetoes</span>
            <strong>{veto.declared} of {veto.needed}</strong>
            <span className="mut">
              kills it, and every counter with it
              {!veto.open && !veto.interpreted && ' — declarations closed at 20 hours'}
              {veto.interpreted && (veto.open
                ? ' — under the draft\u2019s rules this app closes them with the counter window,'
                  + ' which the rulebook does not actually say'
                : ' — closed with the counter window')}
            </span>
          </p>
          <Corrections t={t} />
          {isCommish && !correcting && (
            <span className="trcommishrow">
              <button type="button" className="linkbtn tiny" onClick={() => setCorrecting(true)}>
                Correct the time or terms
              </button>
              {!forcing ? (
                <button type="button" className="linkbtn tiny" onClick={() => setForcing(true)}>
                  Close the window now
                </button>
              ) : (
                /*
                 * The sentence says what is lost, not what is gained. A commissioner reading
                 * "push it through?" agrees with himself; one reading which right he is ending
                 * has to mean it.
                 */
                <span className="trforceask">
                  <span className="mut">
                    {veto.declared > 0
                      ? `Ends the counter and veto period with ${veto.declared} of `
                        + `${veto.needed} declared. `
                      : 'Ends the counter and veto period early. '}
                    It files straight away, at the time it was agreed.
                  </span>
                  <button type="button" className="btn sm" onClick={() => setForcing(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn commish sm" onClick={pushThrough}>
                    Close it now
                  </button>
                </span>
              )}
            </span>
          )}
        </div>
        {/*
          THE MARK ANSWERS THE QUESTION IT IS NEXT TO. It used to open 2016-I-3 — vetoes — while
          the header said "of 48 — offseason", and a reader asking why forty-eight found a rule
          about six of eight. `windowWhy` builds the index for THIS trade's phase, with the
          sentence saying why and, in the offseason, when the draft-day shape takes over.
        */}
        <IndexMark index={windowWhy(t.agreedAt!, ctx, (iso) => bothZones(iso))} />
      </header>

      {correcting && <Correct t={t} ctx={ctx} onDone={() => setCorrecting(false)} />}

      <Counters t={t} ctx={ctx} now={now} isCommish={isCommish} counters={mine}
        onVeto={declareVeto} />
    </section>
  );
}

/* -- a commissioner putting a recorded trade right --------------------------------------------- */

/**
 * Correct a trade that is already in its window.
 *
 * TWO THINGS, AND ONLY TWO. The agreed time, because the window is measured from it and a deal
 * recorded from Discord gets its time typed by a person — 1 September had one counting from
 * 7:46 PM for a trade agreed at 2:23. And the terms, because the same person typed those. Not
 * the parties, not the affirmation, not the vetoes: those are other people's acts.
 *
 * EVERY CORRECTION IS WRITTEN DOWN ON THE TRADE, in words, with a name on it, and the card prints
 * the list. A commissioner who can silently move a deadline is a commissioner nobody can argue
 * with, and this league argues. `firestore.rules` already lets a commissioner update the
 * document; what was missing was a screen that did it honestly rather than a console that did
 * it quietly.
 */
function Correct(
  { t, ctx, onDone }: { t: LiveTradeDoc; ctx: Ctx; onDone: () => void },
) {
  const toLocal = (iso: string) => {
    const d = new Date(iso);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const [agreed, setAgreed] = useState(t.agreedAt ? toLocal(t.agreedAt) : '');
  const [aSends, setASends] = useState<LiveTradeAsset[]>(t.sides[0].sends);
  const [bSends, setBSends] = useState<LiveTradeAsset[]>(t.sides[1].sends);
  const order = ctx.clock?.order ?? [];
  const holdings = (gm: string) => holdingsFor(gm, ctx.ownership, LEAGUE_2026.draftRounds);

  const save = () => {
    const by = ctx.gm.toUpperCase();
    const at = new Date().toISOString();
    const corrections = [...(t.corrections ?? [])];
    const nextAgreed = agreed ? new Date(agreed).toISOString() : t.agreedAt;
    if (nextAgreed && t.agreedAt && nextAgreed !== t.agreedAt) {
      corrections.push({ at, by, field: 'agreedAt', from: stamp(t.agreedAt), to: stamp(nextAgreed) });
    }
    const before = `${assetPhrase(t.sides[0].sends)} for ${assetPhrase(t.sides[1].sends)}`;
    const after = `${assetPhrase(aSends)} for ${assetPhrase(bSends)}`;
    if (before !== after) corrections.push({ at, by, field: 'terms', from: before, to: after });
    if (corrections.length === (t.corrections?.length ?? 0)) { onDone(); return; }
    ctx.onSave({
      ...t,
      agreedAt: nextAgreed,
      sides: [{ ...t.sides[0], sends: aSends }, { ...t.sides[1], sends: bSends }],
      corrections,
    });
    onDone();
  };

  return (
    <div className="trcorrect">
      <label className="trcorrectat">
        <span className="mut">Agreed at</span>
        <input type="datetime-local" value={agreed} onChange={(e) => setAgreed(e.target.value)} />
        <span className="mut">The window is measured from this.</span>
      </label>
      <div className="trcols">
        <div className="trcol">
          <div className="trwho"><GmBadge gm={t.sides[0].gm} withName /><span className="mut tag">sends</span></div>
          <AssetStack assets={aSends} onAssets={setASends} holdings={holdings(t.sides[0].gm)}
            who={t.sides[0].gm} order={order} />
        </div>
        <span className="trswap" aria-hidden="true">⇄</span>
        <div className="trcol">
          <div className="trwho"><GmBadge gm={t.sides[1].gm} withName /><span className="mut tag">sends</span></div>
          <AssetStack assets={bSends} onAssets={setBSends} holdings={holdings(t.sides[1].gm)}
            who={t.sides[1].gm} order={order} />
        </div>
      </div>
      <div className="trcorrectrow">
        <span className="mut">Written on the trade with your name, and shown to everyone.</span>
        <span className="spacer" />
        <button type="button" className="btn sm" onClick={onDone}>Cancel</button>
        <button type="button" className="btn commish sm" onClick={save}>Save correction</button>
      </div>
    </div>
  );
}

/** "Corrected by Tim, 1 Sept, 9:05pm — agreed time 7:46pm → 2:23pm" */
function Corrections({ t }: { t: LiveTradeDoc }) {
  if (!t.corrections?.length) return null;
  return (
    <ul className="trcorrections">
      {t.corrections.map((c, i) => (
        <li key={i} className="mut">
          Corrected by {managerShort(c.by)}, {stamp(c.at)} —
          {' '}{c.field === 'agreedAt' ? 'agreed time' : 'terms'} {c.from} → {c.to}
        </li>
      ))}
    </ul>
  );
}

/* -- counters ---------------------------------------------------------------------------------- */

function Counters(
  { t, ctx, now, isCommish, counters, onVeto }: {
    t: LiveTradeDoc; ctx: Ctx; now: number; isCommish: boolean;
    counters: LiveTradeCounter[]; onVeto: () => void;
  },
) {
  const me = ctx.gm.toUpperCase();
  const bystander = canCounter(t, me, { ...ctx, now });
  /*
   * The one thing that moves in the last four hours. Bystanders are done at hour 20; the tail
   * belongs to the man who was bumped out of the trade this one replaced.
   */
  const match = matchRight(
    t,
    t.derivedFrom ? ctx.originalOf?.(t.derivedFrom) : undefined,
    me,
    { ...ctx, now },
  );
  // Same substitution as the strip above: on draft day "31h left" is a lie about a pick count.
  const onBoard = t.agreedAt ? windowShape(t.agreedAt, ctx).phase === 'draft-day' : false;
  const picks = onBoard ? picksUntilClose(t, t.season, { ...ctx, now }) : null;
  const party = isParty(t, me);
  const [a, b] = t.sides;
  const inPlay = counters.filter((c) => c.state === 'open').length;
  const [composing, setComposing] = useState(false);

  /*
   * ACCEPTING IS NOT A STATUS CHANGE. It discontinues this trade, closes every other offer to the
   * same man, and puts a NEW trade up inheriting this one's clock — 2015-II-2, worked out in
   * `domain/tradeWindow.ts` rather than here, because getting the inheritance wrong is how a deal
   * gets a fresh 48 hours it is not entitled to.
   */
  const accept = (c: LiveTradeCounter) => {
    const out = acceptCounter(
      t, c.id, new Date().toISOString(), (x) => `lt-${t.id}-${x.id}`,
    );
    if (!out) return;
    ctx.onSave(out.spawned);
    ctx.onSave(out.original);
  };
  const decline = (c: LiveTradeCounter) => ctx.onSave(declineCounter(t, c.id));

  return (
    <div className="trcounters">
      <div className="trcounthead">
        <span className="trarrow" aria-hidden="true">↳</span>
        <span className="trcounttitle">
          {match.may
            ? 'Your four hours to match'
            : party ? 'Counter-offers to you'
              : isCommish ? 'Counter-offers in play' : 'Your counter-offer'}
        </span>
        <span className="chip tiny">Private</span>
        {/*
          The count is inside the private block and nowhere else. It was briefly on the public
          window strip, where "two in play" tells the eight uninvolved GMs that somebody is
          bidding — which is precisely what 2015-II-2 makes private.
        */}
        {inPlay > 0 && <span className="mut">{word(inPlay)} in play</span>}
        <span className="spacer" />
        <span className="mut">
          {managerShort(a.gm)} and {managerShort(b.gm)} ·{' '}
          {onBoard
            ? (picks === null ? 'until the pick is made'
              : picks === 0 ? 'the pick is on the clock' : `${picks} picks left`)
            : `${countdown(msLeft(t, { ...ctx, now }))} left`}
        </span>
      </div>

      {/*
        THE COMPOSER IS A FORM, AND A FORM OPENS (Tim, 8 September: "these take up WAY too much
        space. without losing information, make the display concise").
        
        It was open under every live trade, always: three hundred and eighteen pixels of empty
        asset columns and add-buttons, more than half the card, on a page that shows one of these
        per deal in flight. Nobody counters most trades, and the man who wants to counter one is
        making a decision before he types anything — so the decision gets a line and the form
        arrives when he has made it. Nothing is lost: every control is the same, the paragraph
        about what a counter IS moves inside it where it is being read for a reason, and a match —
        the bumped partner's four hours, which is a clock and not an invitation — still opens
        itself, because that one is being offered TO him rather than by him.
      */}
      {(bystander || match.may) && (
        composing || match.may ? (
          <>
            <Composer t={t} ctx={ctx} onVeto={onVeto} tally={vetoTally(t, { ...ctx, now })}
              match={match.may ? match : null} />
            {!match.may && (
              <button type="button" className="linkbtn tiny trcountclose"
                onClick={() => setComposing(false)}>
                Put the offer away
              </button>
            )}
          </>
        ) : (
          <button type="button" className="btn sm trcountopen" onClick={() => setComposing(true)}>
            Make a counter-offer
          </button>
        )
      )}

      {counters.length > 0 && (
        <ul className="trcountlist">
          {counters.map((c) => (
            <li key={c.id} className={`trcountrow ${c.state}`}>
              <div className="trcountwho">
                <GmBadge gm={c.from} withName />
                <span className="mut">sent {ago(c.at, now)}</span>
                {/*
                  Labelled on the RECEIVING side too. To the man deciding, a match and a counter
                  look identical — two assets and two buttons — and they are not the same thing:
                  one is a bystander bidding, the other is the partner he bumped, inside four
                  hours the rulebook set aside for exactly this. He should know which he is
                  turning down.
                */}
                {c.kind === 'match' && (
                  <span className="chip tiny match" title="2015-II-3 — the bumped partner’s four hours">
                    matching
                  </span>
                )}
              </div>
              <div className="trcountdeal">
                <Column label={party ? 'He sends' : `${managerShort(c.from)} sends`} assets={c.sends} />
                <span className="trswap" aria-hidden="true">⇄</span>
                <Column label={party ? 'You send' : `${managerShort(c.to)} sends`} assets={c.receives} />
              </div>
              {c.state !== 'open' ? (
                <span className="mut trcountstate">{c.state}</span>
              ) : party && c.to.toUpperCase() === me ? (
                <span className="trcountact">
                  {/*
                    Accept is one-way (2015-II-2): a GM who accepts cannot go back to an offer he
                    passed on. So it replaces the trade and closes every other offer to him — the
                    rule's own words, not a convenience.
                  */}
                  <button type="button" className="btn primary sm" onClick={() => accept(c)}>
                    Accept
                  </button>
                  <button type="button" className="btn sm" onClick={() => decline(c)}>
                    Decline
                  </button>
                </span>
              ) : (
                <span className="mut trcountact">{managerShort(c.to)}’s call</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/*
        The rule, where it is being read for a reason: inside the form, or beside the offers a man
        is deciding on. Under a closed form it was a paragraph explaining a thing nobody was doing.
      */}
      {(composing || match.may || counters.length > 0) && (
        <p className="mut trcountfoot">
          Counters are private and unlimited, to either GM, and a GM bumped out of the trade may
          keep countering. Accepting one posts it publicly and replaces the trade; declining closes
          that offer for good. <Rule id="2015-II-2" />
        </p>
      )}
    </div>
  );
}

function Column({ label, assets }: { label: string; assets: readonly LiveTradeAsset[] }) {
  return (
    <span className="trcountcol">
      <span className="trcountlab">{label}</span>
      <span className="trcountassets">
        {assets.length === 0
          ? <span className="mut">nothing</span>
          : assets.map((a, i) => <AssetLine key={`${a.kind}-${i}`} a={a} />)}
      </span>
    </span>
  );
}

/* -- the bystander's composer ------------------------------------------------------------------ */

/**
 * One composer, two rights.
 *
 * A bystander's counter (2015-II-2) and the bumped partner's match (2015-II-3) are the same act
 * mechanically — an offer to one man, accepted the same way, replacing the trade the same way —
 * and building a second form for the second one would be two places for "what is an offer" to
 * drift apart. What differs is who may open it, who it may be aimed at, and one requirement:
 *
 *   COUNTER   any bystander, either party, no requirement on what he asks for
 *   MATCH     the bumped partner only, aimed at the man who bumped him, and it must ask for at
 *             least one asset from that man's package in the ORIGINAL trade
 *
 * The requirement is the whole substance of "match or beat". Without it the tail would just be
 * four extra hours of countering for one privileged GM, against a trade he was never in.
 */
function Composer(
  { t, ctx, onVeto, tally, match }:
  {
    t: LiveTradeDoc; ctx: Ctx; onVeto: () => void;
    tally: ReturnType<typeof vetoTally>; match: MatchRight | null;
  },
) {
  const me = ctx.gm.toUpperCase();
  // A match has exactly one possible recipient: the man who is still in the trade.
  const [to, setTo] = useState(match?.against ?? t.sides[0].gm);
  const [sends, setSends] = useState<LiveTradeAsset[]>([]);
  const [receives, setReceives] = useState<LiveTradeAsset[]>([]);
  /*
   * MORE THAN THE PACKAGE. Tim, 1 September: a counter "must include at least one piece of the
   * original trade -- but should be able to include other picks or players." So the receive side
   * is two things: the pick-list off the trade, of which one is required, and a composer for
   * anything else of the other man's — his picks by code, his roster, his farm rights.
   */
  const [extras, setExtras] = useState<LiveTradeAsset[]>([]);

  /*
   * TYPED, NOT TYPED-OUT. The composer shipped with two free-text boxes and that was the one place
   * on this page still doing what the page exists to stop. It matters more here than anywhere: an
   * accepted counter BECOMES a trade and goes into the ledger, so a counter written as a sentence
   * is a ledger row somebody has to re-key, and the re-keying is where "whose ninth is that"
   * stopped being answerable in the first place.
   *
   * A counter takes all five kinds. It took four for a while, on the reasoning that "other
   * consideration" agreed in private is a side agreement — but an accepted counter is posted
   * publicly and replaces the trade, clause and all, so the clause is in the open by the time it
   * binds anybody. Tim, 1 September: "counter offers should also allow for Other considerations."
   */
  const ownership = ctx.ownership;
  const myHoldings = useMemo(
    () => holdingsFor(me, ownership, LEAGUE_2026.draftRounds), [me, ownership],
  );
  const theirHoldings = useMemo(
    () => holdingsFor(to, ownership, LEAGUE_2026.draftRounds), [to, ownership],
  );
  const KINDS = ADD;
  const order = ctx.clock?.order ?? [];
  const phase = tradePhase(Boolean(ctx.inSeason), ctx.keeperDeadline);
  const ownOf = (gm: string) => controlledAssets(
    gm, ROSTER, FARM, ctx.slateOf?.(gm)?.decl, gmOfTeam, phase,
  );
  const myOwn = useMemo(() => ownOf(me), [me, phase]);           // eslint-disable-line react-hooks/exhaustive-deps
  const theirOwn = useMemo(() => ownOf(to), [to, phase]);        // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * WHAT HE MAY ASK FOR IS WHAT IS IN THE TRADE, and nothing else. 2015-II-2 lets a bystander bid
   * on "any single piece of the deal" — it does not let him reach into the other man's roster. So
   * the receive side is a pick-list off the trade's own assets rather than a second composer, and
   * "you do not have to take the whole package" stops being a claim and becomes the interface.
   */
  /*
   * What he may ask for. A counter is limited to what is in the trade in front of him; a match
   * may also reach back to the ORIGINAL package, because that is the thing he is entitled to bid
   * for and it is not necessarily still on the table — the accepted counter may have taken only
   * part of it.
   */
  /*
   * HIS PACKAGE, NOT THE WHOLE DEAL. Tim, 2 September: "what i can receive from the original
   * trade should change depending on who I'm offering to from their original package." A counter
   * to John is "take mine instead of Luke's" — so what is on the table is what JOHN was sending,
   * which is what he will have in hand if he takes it. Offering him his own incoming pick back
   * was never a thing he could give.
   */
  const target = t.sides.find((s) => s.gm.toUpperCase() === to.toUpperCase()) ?? t.sides[0];
  /*
   * FAAB IS NOT A PIECE OF THE DEAL. Tim, 3 September: "remove FAAB from one of the assets that
   * can be selected as one of the original assets when placing a counter." Dollars are a
   * sweetener, not the thing 2015-II-2 lets a bystander bid for — a counter that asks for the
   * $5 and nothing else is a bid for nothing. FAAB can still ride along as an extra below.
   */
  const onOffer = (match ? match.mayAskFor : target.sends).filter((a) => a.kind !== 'faab');
  const required = match
    ? match.mustInclude.filter((m) => onOffer.some((a) => a.kind === m.kind && a.text === m.text))
    : [];
  const isRequired = (a: LiveTradeAsset) =>
    required.some((m) => m.kind === a.kind && m.text === a.text);
  const has = (a: LiveTradeAsset) => receives.some((r) => r.kind === a.kind && r.text === a.text);
  const toggle = (a: LiveTradeAsset) => setReceives(
    has(a) ? receives.filter((r) => !(r.kind === a.kind && r.text === a.text)) : [...receives, a],
  );

  const qualifies = !match || matchQualifies(receives, match.mustInclude);
  /*
   * FARM RIGHTS NEED A FARM SLOT (2022 §1), in both directions: what he sends lands on the other
   * man's roster, what he asks for lands on his. Refused, not warned — a counter the league
   * cannot execute is not an offer.
   */
  const farmBlock = useMemo(() => {
    const rights = (assets: readonly LiveTradeAsset[]) => assets.filter((a) => a.kind === 'rights').length;
    const asked = [...receives, ...extras];
    const toHim = farmCapCheck(managerShort(to), farmCount(to, FARM), rights(sends), rights(asked), LEAGUE_2026.maxFarmPlayers);
    const toMe = farmCapCheck(managerShort(me), farmCount(me, FARM), rights(asked), rights(sends), LEAGUE_2026.maxFarmPlayers);
    return [toHim, toMe].find((c) => !c.ok) ?? null;
  }, [sends, receives, extras, to, me]);
  /* At least one piece of the deal in front of him, or it is a trade of its own. */
  const ready = sends.length > 0 && receives.length > 0 && qualifies && !farmBlock;
  const send = () => {
    if (!ready) return;
    ctx.onSave({
      ...t,
      counters: [...t.counters, {
        id: `c-${Date.now().toString(36)}`,
        from: me,
        to,
        sends,
        receives: [...receives, ...extras],
        at: new Date().toISOString(),
        state: 'open',
        kind: match ? 'match' : 'counter',
      }],
    });
    setSends([]); setReceives([]); setExtras([]);
  };

  return (
    <div className="trcompose">
      <div className="trcomposetop">
        <span className="mut">Offer to</span>
        {match ? (
          <span className="trtoggle one">
            <span className="on">{managerShort(match.against!)}</span>
          </span>
        ) : (
          <span className="trtoggle">
            {t.sides.map((s) => (
              <button type="button" key={s.gm} className={to === s.gm ? 'on' : ''}
                onClick={() => { setTo(s.gm); setReceives([]); setExtras([]); }}>{managerShort(s.gm)}</button>
            ))}
          </span>
        )}
        <span className="mut">
          {match
            ? `your four hours to match — ${countdown(msLeft(t, ctx))} left`
            : `for ${assetPhrase(t.sides[to === t.sides[0].gm ? 0 : 1].sends)}`}
        </span>
      </div>

      <div className="trcomposecols">
        <div className="trcol">
          <span className="trcountlab">{managerShort(me)} sends</span>
          <AssetStack assets={sends} onAssets={setSends} holdings={myHoldings} who={me}
            kinds={KINDS} order={order} own={myOwn} />
        </div>
        <span className="trswap" aria-hidden="true">⇄</span>
        <div className="trcol">
          <span className="trcountlab">{managerShort(me)} receives</span>
          <div className="trpicklist">
            {onOffer.map((a, i) => (
              <button type="button" key={`${a.kind}-${i}`}
                className={`trpickable ${has(a) ? 'on' : ''} ${isRequired(a) ? 'req' : ''}`}
                onClick={() => toggle(a)} aria-pressed={has(a)}>
                <AssetLine a={a} />
                {/* Marked rather than pre-selected: which of his things you go after is the
                    decision, and choosing it for him would make the tail a formality. */}
                {isRequired(a) && <span className="trreq">was in the original</span>}
              </button>
            ))}
          </div>
          <span className="mut trhint">
            {match
              ? 'Compose whatever you like. It only has to ask for at least one thing that was in '
                + `${managerShort(match.against!)}’s original package — otherwise it is a new trade `
                + 'rather than a match, and it does not belong in these four hours.'
              : 'Bid on any single piece of the deal — you do not have to take the whole package. '
                + 'At least one piece has to come from the trade; beyond that you may ask for '
                + `anything else of ${managerShort(to)}’s.`}
          </span>
          {/*
            AND ANYTHING ELSE OF HIS, once one piece of the deal is in. Same editor as the filing
            form, so his picks come by code with the keeper standing on them named, and the extras
            travel with the counter — an accepted counter becomes a trade, and a trade has to say
            whose ninth that is.
          */}
          <div className="trextras">
            <span className="trcountlab">and, beyond the deal</span>
            <AssetStack assets={extras} onAssets={setExtras} holdings={theirHoldings} who={to}
              kinds={ADD.filter((k) => k.kind !== 'clause' && k.kind !== 'faab')} order={order}
              own={theirOwn} disabled={receives.length === 0} />
          </div>
          {farmBlock && (
            <span className="trneeds">{farmBlock.why} <Rule id="2022-farm-auction-declarations" /></span>
          )}
          {match && !qualifies && receives.length > 0 && (
            <span className="trneeds">
              Ask for one of {required.map((a) => a.text).join(' or ')} to make this a match.
              {' '}<Rule id="2015-II-3" />
            </span>
          )}
          {/* The origin travels with the pick he is asking for, so the ledger row an acceptance
              writes says whose seat empties without anybody re-deriving it. */}
          {theirHoldings.length === 0 && (
            <span className="mut trhint">No 2026 picks on record for {managerShort(to)}.</span>
          )}
        </div>
      </div>

      <div className="trcomposefoot">
        <p className="mut">
          {match
            ? `Private to ${managerShort(to)}. He has until the window closes to take it; nobody `
              + 'else is told it exists.'
            : `Private to ${managerShort(to)}. Neither the other party nor the rest of the league `
              + 'is told it exists.'}
        </p>
        <span className="trcomposeacts">
          {/* A veto is a bystander's move. The bumped partner was a party to the trade this one
              replaced, and 2016-I-3 counts only the uninvolved. */}
          {/*
            WHY IT IS GREY, on the button. Tim, 2 September, on a trade he had recorded for two
            other men: "I should still be able to cast a veto vote." He could — a commissioner
            filing on somebody's behalf is not a party, and nothing here excludes the filer. The
            button was grey because 2016-I-3 closes vetoes at twenty hours and the trade was
            twenty-six in, which the header said and the button did not. A disabled control with
            no reason on it reads as a bug about the person looking at it.
          */}
          {!match && (
            <button type="button" className="btn danger sm" onClick={onVeto}
              disabled={!tally.open || t.vetoes.includes(me)}
              title={t.vetoes.includes(me) ? 'You have declared yours.'
                : !tally.open ? 'Vetoes closed at 20 hours (2016-I-3).' : undefined}>
              {t.vetoes.includes(me) ? 'Veto declared'
                : !tally.open ? 'Vetoes closed at 20h'
                  : 'Declare a veto'} · {tally.declared} of {tally.needed}
            </button>
          )}
          <button type="button" className="btn primary" disabled={!ready} onClick={send}>
            {match ? `Send it to ${managerShort(to)}` : `Send privately to ${managerShort(to)}`}
          </button>
        </span>
      </div>
    </div>
  );
}

/* -- both cards share the two-column "gets" block ---------------------------------------------- */

function Gets({ t }: { t: LiveTradeDoc }) {
  const [a, b] = t.sides;
  return (
    <div className="trgets">
      {/* Each column is what that man GETS, which is the other man's sends — the way it is
          announced in the channel, and the reverse of how it is stored. */}
      <Side gm={a.gm} assets={b.sends} />
      <Side gm={b.gm} assets={a.sends} />
    </div>
  );
}

function Side({ gm, assets }: { gm: string; assets: readonly LiveTradeAsset[] }) {
  return (
    <div className="trgetcol">
      <GmBadge gm={gm} />
      <div className="trgetbody">
        <span className="trgetlab">{managerShort(gm)} gets</span>
        {assets.length === 0
          ? <span className="mut">nothing recorded</span>
          : assets.map((a, i) => <AssetLine key={`${a.kind}-${i}`} a={a} />)}
      </div>
    </div>
  );
}

/* -- small ------------------------------------------------------------------------------------- */

const stamp = (iso: string) => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
}).format(new Date(iso)).replace(' am', 'am').replace(' pm', 'pm');

function ago(iso: string, now: number): string {
  const mins = Math.round((now - Date.parse(iso)) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const word = (n: number) => WORDS[n] ?? String(n);

export { parties };
